import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Plus, Bed, User } from 'lucide-react';
import { GET_HABITACIONES_CON_OCUPACION, REGISTRAR_LIMPIEZA } from '../graphql/habitaciones';
import { GET_TIPOS_HABITACION_CONFIG } from '../graphql/configuracion';
import Loading from '../components/shared/Loading';
import Badge from '../components/shared/Badge';
import Button from '../components/shared/Button';
import Select from '../components/shared/Select';
import ConfirmModal from '../components/shared/ConfirmModal';
import SuccessModal from '../components/shared/SuccessModal';
import HabitacionModal from '../components/habitaciones/HabitacionModal';
import HabitacionDetalleModal from '../components/habitaciones/HabitacionDetalleModal';
import './Habitaciones.css';

function Habitaciones() {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    estado: '',
    piso: '',
    tipo: '',
    conCamasLibres: false
  });

  // Estados para modales
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [showConfirmLimpieza, setShowConfirmLimpieza] = useState(false);
  const [habitacionSeleccionada, setHabitacionSeleccionada] = useState(null);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });

  const { data, loading, error, refetch } = useQuery(GET_HABITACIONES_CON_OCUPACION, {
    pollInterval: 10000,
    fetchPolicy: 'cache-and-network'
  });

  const { data: tiposData } = useQuery(GET_TIPOS_HABITACION_CONFIG, {
    variables: { activo: true }
  });

  const tiposFilterOptions = [
    { value: '', label: 'Todos los tipos' },
    ...(tiposData?.tiposHabitacionConfig || []).map(t => ({
      value: t.codigo,
      label: t.nombre
    }))
  ];

  const [registrarLimpieza, { loading: registrandoLimpieza }] = useMutation(REGISTRAR_LIMPIEZA, {
    refetchQueries: [{ query: GET_HABITACIONES_CON_OCUPACION }],
    onCompleted: () => {
      setShowConfirmLimpieza(false);
      setHabitacionSeleccionada(null);
    },
    onError: (error) => {
      console.error('Error al registrar limpieza:', error);
      setErrorModal({ isOpen: true, message: `Error al registrar limpieza: ${error.message}` });
    }
  });

  if (loading && !data) return <Loading fullScreen />;
  if (error) return <div className="error-message">Error al cargar habitaciones: {error.message}</div>;

  const habitaciones = data?.habitacionesConOcupacion || [];

  // Totales de camas
  const totalCamas = habitaciones.reduce((sum, h) => sum + h.capacidad, 0);
  const camasOcupadas = habitaciones.reduce((sum, h) => sum + h.camas_ocupadas, 0);

  // Filtrar habitaciones
  const habitacionesFiltradas = habitaciones.filter(hab => {
    if (filters.estado && hab.estado !== filters.estado) return false;
    if (filters.piso && hab.piso !== parseInt(filters.piso)) return false;
    if (filters.tipo && hab.tipo !== filters.tipo) return false;
    if (filters.conCamasLibres && hab.camas_disponibles <= 0) return false;
    return true;
  });

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };



  // Handlers para modales
  const handleCrearHabitacion = () => {
    setHabitacionSeleccionada(null);
    setShowCrearModal(true);
  };

  const handleVerDetalle = (habitacion) => {
    setHabitacionSeleccionada(habitacion);
    setShowDetalleModal(true);
  };

  const handleAsignarHabitacion = (habitacion) => {
    navigate('/hospedajes', { state: { habitacionPreseleccionada: habitacion } });
  };

  const handleMarcarLimpia = (habitacion) => {
    setHabitacionSeleccionada(habitacion);
    setShowConfirmLimpieza(true);
  };

  const confirmarLimpieza = async () => {
    if (!habitacionSeleccionada) return;
    await registrarLimpieza({
      variables: {
        habitacionId: parseInt(habitacionSeleccionada.id)
      }
    });
  };

  // Barra de ocupación de camas
  const renderOcupacionBar = (hab) => {
    const { capacidad, camas_ocupadas } = hab;
    const slots = [];
    for (let i = 0; i < capacidad; i++) {
      slots.push(
        <div
          key={i}
          className={`cama-slot ${i < camas_ocupadas ? 'cama-slot--ocupada' : 'cama-slot--libre'}`}
          title={i < camas_ocupadas ? `Cama ${i + 1}: Ocupada` : `Cama ${i + 1}: Libre`}
        />
      );
    }
    return <div className="camas-bar">{slots}</div>;
  };

  return (
    <div className="habitaciones-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Gestión de Habitaciones</h1>
            <p className="page-subtitle">Administra el inventario de habitaciones del hotel</p>
          </div>
          <button className="btn-nueva-habitacion" onClick={handleCrearHabitacion}>
            <Plus size={18} />
            Nueva Habitación
          </button>
        </div>

        {/* Resumen de camas */}
        <div className="camas-resumen">
          <div className="camas-resumen__item">
            <Bed size={16} />
            <span><strong>{camasOcupadas}</strong> / {totalCamas} camas ocupadas</span>
          </div>
          <div className="camas-resumen__bar">
            <div
              className="camas-resumen__fill"
              style={{ width: totalCamas > 0 ? `${(camasOcupadas / totalCamas) * 100}%` : '0%' }}
            />
          </div>
          <span className="camas-resumen__pct">
            {totalCamas > 0 ? Math.round((camasOcupadas / totalCamas) * 100) : 0}%
          </span>
        </div>

        <div className="filters-section">
          <Select
            label="Estado"
            name="estado"
            value={filters.estado}
            onChange={handleFilterChange}
            options={[
              { value: '', label: 'Todos los estados' },
              { value: 'disponible', label: 'Disponible' },
              { value: 'ocupada', label: 'Ocupada' },
              { value: 'limpieza', label: 'En Limpieza' },
              { value: 'mantenimiento', label: 'Mantenimiento' },
              { value: 'reservada', label: 'Reservada' }
            ]}
          />

          <Select
            label="Piso"
            name="piso"
            value={filters.piso}
            onChange={handleFilterChange}
            options={[
              { value: '', label: 'Todos los pisos' },
              { value: '1', label: 'Piso 1' },
              { value: '2', label: 'Piso 2' },
              { value: '3', label: 'Piso 3' },
              { value: '4', label: 'Piso 4' }
            ]}
          />

          <Select
            label="Tipo"
            name="tipo"
            value={filters.tipo}
            onChange={handleFilterChange}
            options={tiposFilterOptions}
          />

          <div className="filter-actions">
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={filters.conCamasLibres}
                onChange={(e) => setFilters({ ...filters, conCamasLibres: e.target.checked })}
              />
              <span>Con camas libres</span>
            </label>
            <Button
              variant="secondary"
              onClick={() => setFilters({ estado: '', piso: '', tipo: '', conCamasLibres: false })}
            >
              Limpiar
            </Button>
          </div>
        </div>

        <div className="habitaciones-stats">
          <span className="stats-text">
            Mostrando {habitacionesFiltradas.length} de {habitaciones.length} habitaciones
          </span>
        </div>

        {habitacionesFiltradas.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🛏️</div>
            <h3>No se encontraron habitaciones</h3>
            <p>
              {habitaciones.length === 0
                ? 'Comienza agregando habitaciones a tu inventario hotelero.'
                : 'Intenta ajustar los filtros para ver más resultados.'}
            </p>
            {habitaciones.length === 0 && (
              <Button variant="primary" onClick={handleCrearHabitacion}>
                Crear Primera Habitación
              </Button>
            )}
          </div>
        ) : (
          <div className="habitaciones-grid">
            {habitacionesFiltradas.map((habitacion) => (
              <div key={habitacion.id} className={`habitacion-card ${habitacion.camas_ocupadas > 0 && habitacion.camas_disponibles > 0 ? 'habitacion-card--parcial' : ''}`}>
                <div className="habitacion-header">
                  <div>
                    <h3 className="habitacion-numero">Habitación {habitacion.numero}</h3>
                    <p className="habitacion-tipo">{habitacion.tipo.charAt(0).toUpperCase() + habitacion.tipo.slice(1)}</p>
                  </div>
                  <Badge variant={habitacion.estado}>
                    {habitacion.estado}
                  </Badge>
                </div>

                <div className="habitacion-info">
                  <div className="info-item">
                    <span className="info-label">Piso:</span>
                    <span className="info-value">{habitacion.piso}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Camas:</span>
                    <span className="info-value">
                      {habitacion.camas_ocupadas > 0
                        ? `${habitacion.camas_ocupadas} / ${habitacion.capacidad} ocupadas`
                        : `${habitacion.capacidad} ${habitacion.capacidad === 1 ? 'cama' : 'camas'}`
                      }
                    </span>
                  </div>
                </div>

                {/* Barra visual de camas */}
                {renderOcupacionBar(habitacion)}

                {/* Mini lista de ocupantes */}
                {habitacion.ocupantes_activos && habitacion.ocupantes_activos.length > 0 && (
                  <div className="ocupantes-mini">
                    {habitacion.ocupantes_activos.slice(0, 4).map((oc) => (
                      <div key={oc.id} className="ocupante-mini-item">
                        <User size={12} />
                        <span className="ocupante-mini-cama">#{oc.cama_numero}</span>
                        <span className="ocupante-mini-nombre">{oc.nombre}</span>
                        {oc.grado_nombre && <span className="ocupante-mini-grado">{oc.grado_nombre}</span>}
                      </div>
                    ))}
                    {habitacion.ocupantes_activos.length > 4 && (
                      <span className="ocupantes-mini-more">+{habitacion.ocupantes_activos.length - 4} más</span>
                    )}
                  </div>
                )}

                <div className="habitacion-actions">
                  <Button variant="outline" size="sm" fullWidth onClick={() => handleVerDetalle(habitacion)}>
                    Ver Detalles
                  </Button>
                  {habitacion.estado === 'disponible' && (
                    <Button variant="primary" size="sm" fullWidth onClick={() => handleAsignarHabitacion(habitacion)}>
                      Check-In
                    </Button>
                  )}
                  {habitacion.estado === 'limpieza' && (
                    <Button variant="secondary" size="sm" fullWidth onClick={() => handleMarcarLimpia(habitacion)}>
                      Marcar Limpia
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modales */}
        <HabitacionModal
          isOpen={showCrearModal}
          onClose={() => setShowCrearModal(false)}
          onSuccess={refetch}
        />

        <HabitacionModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setHabitacionSeleccionada(null);
          }}
          habitacion={habitacionSeleccionada}
          onSuccess={refetch}
        />

        <HabitacionDetalleModal
          isOpen={showDetalleModal}
          onClose={() => {
            setShowDetalleModal(false);
            setHabitacionSeleccionada(null);
          }}
          habitacion={habitacionSeleccionada}
          onEdit={(hab) => {
            setShowDetalleModal(false);
            setTimeout(() => {
              setHabitacionSeleccionada(hab);
              setShowEditModal(true);
            }, 0);
          }}
          onSuccess={refetch}
        />

        <ConfirmModal
          isOpen={showConfirmLimpieza}
          onClose={() => {
            setShowConfirmLimpieza(false);
            setHabitacionSeleccionada(null);
          }}
          onConfirm={confirmarLimpieza}
          title="Marcar habitación como limpia"
          message={`¿Confirma que la limpieza de la habitación ${habitacionSeleccionada?.numero} ha sido completada?`}
          confirmText="Sí, marcar limpia"
          cancelText="Cancelar"
          variant="success"
          loading={registrandoLimpieza}
        />

        {/* Modal de error */}
        <SuccessModal
          isOpen={errorModal.isOpen}
          onClose={() => setErrorModal({ isOpen: false, message: '' })}
          type="error"
          message={errorModal.message}
        />
      </div>
  );
}

export default Habitaciones;
